import React, { useEffect, useState } from 'react';
import { ActivityIcon, AlertTriangleIcon, CheckIcon, DatabaseIcon, ImageIcon, InfoIcon, KeyRoundIcon, RefreshCwIcon, UploadIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Tabs } from '../../components/ui/Tabs';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { PermissionGate } from '../../components/admin/PermissionGate';
import { usePlatformConfig } from '../../contexts/PlatformConfigContext';
import { useGovernance } from '../../contexts/GovernanceContext';
import { useSectionRoute } from '../../hooks/useSectionRoute';
import { TERMINOLOGY, TERM_CONCEPTS, resolveTerm } from '../../data/terminology';
import type { TermConcept, TerminologyOverrides } from '../../types/install';
import { checkAdminSlug, RESERVED_ROUTES } from '../../data/reservedRoutes';
import { ALLOWED_BRANDING_TYPES, MAX_BRANDING_BYTES, SETTINGS_ARE_SERVER_BACKED, databaseHealth, settingsHistory, systemInformation, uploadBranding } from '../../services/platformSettingsStore';
import type { DatabaseHealth, LocalizationSettings, PlatformIdentity, SecuritySettings, SeoSettings, SettingsChange, SettingsSection, SystemInformation } from '../../types/install';

/**
 * Platform settings.
 *
 * Everything the installer asked once is editable here — and two things are
 * deliberately not: database credentials and server secrets. They are absent
 * from the types this page can reach, so their absence is structural rather
 * than a decision this screen makes.
 *
 * Every section saves on its own, behind a stated reason, because a settings
 * write that half-applies could pair a new site title with an old security
 * policy. Nothing publishes on a control interaction.
 */

const SECTIONS = ['Platform', 'Terminology', 'Website & SEO', 'Branding', 'Localisation', 'Security', 'Admin access', 'Database', 'System information'] as const;
type Section = (typeof SECTIONS)[number];
export function PlatformSettings() {
  const [section, setSection] = useSectionRoute<Section>('/admin/settings', SECTIONS);
  const {
    source,
    error,
    refresh
  } = usePlatformConfig();
  return <div className="space-y-6" data-id="element-7217" data-mp="14txbzo494ymb">
      <PageHeader eyebrow="Platform" title="Settings" description="Platform identity, website metadata, branding, localisation, security policy and where administration answers. Changing any of these never needs the front end rebuilt." actions={<Button variant="secondary" icon={<RefreshCwIcon className="h-4 w-4" data-id="element-7220" data-mp="2p8z9zcla2u9y" />} onClick={refresh} data-id="element-7219" data-mp="qv61scsifx3g">
            Reload configuration
          </Button>} data-id="element-7218" data-mp="14r1hk9rm4s2m" />

      <SourceNotice source={source} error={error} data-id="element-7221" data-mp="2l8fesek9m0qg" />

      <Tabs tabs={SECTIONS} value={section} onChange={setSection} label="Settings sections" data-id="element-7222" data-mp="2l5jjkxzsxq5p" />

      {section === 'Platform' && <PlatformSection data-id="element-7223" data-mp="byfz4c97pw1l" />}
      {section === 'Terminology' && <TerminologySection data-id="element-7224" data-mp="bwv98zxuf1a2" />}
      {section === 'Website & SEO' && <SeoSection data-id="element-7225" data-mp="gp69k3whwp35" />}
      {section === 'Branding' && <BrandingSection data-id="element-7226" data-mp="griw6ud77nmj" />}
      {section === 'Localisation' && <LocalizationSection data-id="element-7227" data-mp="kpyhmttmsnxu" />}
      {section === 'Security' && <SecuritySection data-id="element-7228" data-mp="3k64vkjjhynk9" />}
      {section === 'Admin access' && <AdminAccessSection data-id="element-7229" data-mp="3k7ppq81wg4hl" />}
      {section === 'Database' && <DatabaseSection data-id="element-7230" data-mp="3p4ycklxzswp6" />}
      {section === 'System information' && <SystemSection data-id="element-7231" data-mp="3p7c3ciln3mqx" />}

      {section !== 'Database' && section !== 'System information' && <HistoryPanel data-id="element-7232" data-mp="3t5i5fdbj9e5i" />}
    </div>;
}

/* ------------------------------------------------------------------ source */

function SourceNotice({
  source,
  error
}: {
  source: 'server' | 'local' | 'defaults';
  error: string | null;
}) {
  if (source === 'server') {
    return <div className="flex flex-wrap items-center gap-3 rounded-lg border border-ok-200 bg-ok-50 p-4" data-id="element-7233" data-mp="1dedrz31tii2u">
        <Badge tone="ok" icon={<CheckIcon className="h-3.5 w-3.5" data-id="element-7235" data-mp="1c0m7wb8edshl" />} data-id="element-7234" data-mp="1bzb78ka910up">
          Server-backed — published
        </Badge>
        <p className="min-w-0 text-body-sm text-ink-muted" data-id="element-7236" data-mp="171je5mxvb8ox">
          These values come from the configuration service and apply to every visitor.
        </p>
      </div>;
  }
  return <div className="rounded-lg border border-warn-200 bg-warn-50 p-4" data-id="element-7237" data-mp="1gwb90j02fhg0">
      <div className="flex flex-wrap items-center gap-3" data-id="element-7238" data-mp="1uwcd07bmn4zp">
        <Badge tone="warn" icon={<AlertTriangleIcon className="h-3.5 w-3.5" data-id="element-7240" data-mp="1pz2oquzz4zdg" />} data-id="element-7239" data-mp="1pyarlfqqatl2">
          {source === 'local' ? 'This browser only — not published' : 'Development defaults'}
        </Badge>
        <p className="min-w-0 text-body-sm text-ink-muted" data-id="element-7241" data-mp="24p09f0yp36xw">
          {SETTINGS_ARE_SERVER_BACKED ? 'The configuration service did not answer, so this is the last configuration this browser saw. A change saved now reaches this browser only.' : 'No configuration service is configured for this deployment, so a saved change reaches this browser and its tabs only. Another browser, device or private window sees platform defaults.'}
        </p>
      </div>
      {error && <p className="mt-2 text-body-sm text-ink-muted" data-id="element-7242" data-mp="2x7kq947p6n5k">Reported: {error}</p>}
      <p className="mt-2 text-caption text-ink-subtle" data-id="element-7243" data-mp="2j9cn2ek2iply">
        Connect the API origin — see INSTALLATION.md — and the same screens become authoritative for
        every visitor without any change here.
      </p>
    </div>;
}

/* ------------------------------------------------------- section scaffolding */

/**
 * One saveable section. Owns the draft, what differs, the required reason and
 * the warning before leaving with something outstanding — so no individual
 * section has to reimplement any of it.
 */
function SectionForm<T>({
  section,
  permission,
  title,
  description,
  live,
  children,
  validate,
  summarise
}: {
  section: SettingsSection;
  permission: string;
  title: string;
  description: string;
  live: T;
  children: (draft: T, set: (next: T) => void) => React.ReactNode;
  validate?: (draft: T) => string | null;
  summarise: (draft: T) => Array<{
    field: string;
    from: string;
    to: string;
  }>;
}) {
  const {
    save
  } = usePlatformConfig();
  const {
    me,
    logAction
  } = useGovernance();
  const [draft, setDraft] = useState<T>(live);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const changes = summarise(draft);
  const dirty = changes.length > 0;
  const problem = validate?.(draft) ?? null;

  // A change made elsewhere replaces an untouched draft rather than leaving
  // this editor quietly out of date.
  useEffect(() => {
    if (!dirty) setDraft(live);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  async function commit() {
    setSaving(true);
    setFailure(null);
    try {
      await save(section, draft, reason.trim(), me.name);
      logAction({
        action: `Changed ${title.toLowerCase()}`,
        resource: 'Platform settings',
        resourceId: section,
        before: changes.map(change => `${change.field}: ${change.from}`).join(' · ') || '—',
        after: changes.map(change => `${change.field}: ${change.to}`).join(' · ') || '—',
        reason: reason.trim()
      });
      setConfirming(false);
      setReason('');
      setSaved(true);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'The change could not be saved.');
    } finally {
      setSaving(false);
    }
  }
  return <PermissionGate permission={permission} label={`change ${title.toLowerCase()}`} data-id="element-7244" data-mp="8o49v8mihbyj">
      <Panel title={title} description={description} data-id="element-7245" data-mp="3lvgje6yarahz">
        {children(draft, next => {
        setDraft(next);
        setSaved(false);
      })}

        {problem && <p className="mt-4 flex items-start gap-2 text-body-sm text-danger-700" data-id="element-7246" data-mp="3upbf6odj3oky">
            <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-7247" data-mp="3qnq1x1x318a8" />
            {problem}
          </p>}

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-line pt-4" data-id="element-7248" data-mp="32yx4t24q4ue1">
          {dirty ? <>
              <Badge tone="warn" data-id="element-7249" data-mp="2y0ncczadw7w4">
                {changes.length} unsaved {changes.length === 1 ? 'change' : 'changes'}
              </Badge>
              <p className="min-w-0 flex-1 text-body-sm text-ink-muted" data-id="element-7250" data-mp="36yzadmdky7yl">
                Nothing has changed for anyone yet.
              </p>
              <Button variant="ghost" onClick={() => setDraft(live)} data-id="element-7251" data-mp="r6rv49507p18">
                Discard
              </Button>
              <Button onClick={() => setConfirming(true)} disabled={Boolean(problem)} data-id="element-7252" data-mp="mbci1jetlx5v">
                Save changes
              </Button>
            </> : <p className="text-body-sm text-ink-muted" data-id="element-7253" data-mp="ysna3wgubt5v">
              {saved ? 'Saved and applied.' : 'No outstanding changes.'}
            </p>}
        </div>
      </Panel>

      <Modal open={confirming} onClose={() => setConfirming(false)} title={`Save ${title.toLowerCase()}?`} description="Recorded against your name in the audit log with the previous and new values." footer={<>
            <Button variant="ghost" onClick={() => setConfirming(false)} data-id="element-7255" data-mp="13qykc8b1s5om">
              Cancel
            </Button>
            <Button onClick={commit} loading={saving} disabled={reason.trim().length < 4} data-id="element-7256" data-mp="18pi7gc1dnnik">
              Save changes
            </Button>
          </>} data-id="element-7254" data-mp="pqe51m4ccrzv">
        <dl className="divide-y divide-line rounded-lg border border-line" data-id="element-7257" data-mp="1wdt2uzob0uzg">
          {changes.map(change => <div key={change.field} className="grid gap-1 p-3 sm:grid-cols-[160px_1fr]" data-id="element-7258" data-mp="3rbyvjmug1cfm">
              <dt className="text-body-sm font-semibold text-ink" data-id="element-7259" data-mp="3ra5mk0wcyg4g">{label(change.field)}</dt>
              <dd className="min-w-0 break-words text-body-sm text-ink-muted" data-id="element-7260" data-mp="3bdbfri25ca">
                <span className="line-through" data-id="element-7261" data-mp="3ib0sp7vqraym">{change.from || '—'}</span>{' '}
                <span className="text-ink" data-id="element-7262" data-mp="3n92a7odir27z">→ {change.to || '—'}</span>
              </dd>
            </div>)}
        </dl>
        <div className="mt-4" data-id="element-7263" data-mp="38je84kxijzj7">
          <Field label="Why is this changing?" hint="A reason of substance is required." data-id="element-7264" data-mp="3dbxo1ojob6r6">
            <Textarea rows={3} value={reason} onChange={event => setReason(event.target.value)} data-id="element-7265" data-mp="2zksqqt3s2qk0" />
          </Field>
        </div>
        {failure && <p className="mt-3 text-body-sm text-danger-700" data-id="element-7266" data-mp="33n7d3n6no8g1">{failure}</p>}
      </Modal>
    </PermissionGate>;
}
function label(field: string): string {
  return field.replace(/([A-Z])/g, ' $1').replace(/^./, character => character.toUpperCase()).trim();
}
function diffOf<T extends Record<string, unknown>>(before: T, after: T): Array<{
  field: string;
  from: string;
  to: string;
}> {
  return Object.keys(after).filter(key => String(before[key] ?? '') !== String(after[key] ?? '')).map(key => ({
    field: key,
    from: String(before[key] ?? ''),
    to: String(after[key] ?? '')
  }));
}

/* -------------------------------------------------------------- 1. platform */

function PlatformSection() {
  const {
    config
  } = usePlatformConfig();
  return <SectionForm<PlatformIdentity> section="platform" permission="platform.settings.manage" title="Platform identity" description="Who this deployment is. Used in the interface, in transactional mail and in link previews." live={config.platform} validate={draft => draft.name.trim().length < 2 ? 'A platform name is required.' : null} summarise={draft => diffOf(config.platform as never, draft as never)} data-id="element-7267" data-mp="2p08os133hu5j">
      {(draft, set) => <div className="grid gap-4 md:grid-cols-2" data-id="element-7268" data-mp="3fref7sxfp3fm">
          <Field label="Platform name" data-id="element-7269" data-mp="3forzv74axjmu">
            <Input value={draft.name} onChange={e => set({
          ...draft,
          name: e.target.value
        })} data-id="element-7270" data-mp="3kpg26oxzdmwd" />
          </Field>
          <Field label="Short name" hint="Used where space is tight — navigation, tabs." data-id="element-7271" data-mp="3po120n9bm4ka">
            <Input value={draft.shortName} onChange={e => set({
          ...draft,
          shortName: e.target.value
        })} data-id="element-7272" data-mp="3ug0vbwuvxz7w" />
          </Field>
          <Field label="Organisation" data-id="element-7273" data-mp="2eckfol2s82l">
            <Input value={draft.organization} onChange={e => set({
          ...draft,
          organization: e.target.value
        })} data-id="element-7274" data-mp="7a9vbfdkkm7z" />
          </Field>
          <Field label="Primary domain" data-id="element-7275" data-mp="h8z5sn34z4e7">
            <Input value={draft.domain} onChange={e => set({
          ...draft,
          domain: e.target.value
        })} data-id="element-7276" data-mp="h7xv4jq18jx0" />
          </Field>
          <Field label="Support email" hint="Where students are told to write." data-id="element-7277" data-mp="2e3demo1vqv9f">
            <Input type="email" value={draft.supportEmail} onChange={e => set({
          ...draft,
          supportEmail: e.target.value
        })} data-id="element-7278" data-mp="2j1ezneiv94nj" />
          </Field>
        </div>}
    </SectionForm>;
}

/* ------------------------------------------------------------- 2. seo */

function SeoSection() {
  const {
    config
  } = usePlatformConfig();
  return <SectionForm<SeoSettings> section="seo" permission="platform.settings.manage" title="Website and sharing" description="Applied to the document head at runtime. A change here takes effect on the next load — never a rebuild." live={config.seo} validate={draft => draft.metaDescription.trim().length < 40 ? 'A meta description of at least 40 characters is required — shorter than that is not a description.' : null} summarise={draft => diffOf(config.seo as never, draft as never)} data-id="element-7279" data-mp="mfe10r3jke4f">
      {(draft, set) => <div className="space-y-4" data-id="element-7280" data-mp="3pu1of70aw0bf">
          <div className="grid gap-4 md:grid-cols-2" data-id="element-7281" data-mp="3pww5qpo0p50m">
            <Field label="Website title" data-id="element-7282" data-mp="1hl272nu8t64e">
              <Input value={draft.siteTitle} onChange={e => set({
            ...draft,
            siteTitle: e.target.value
          })} data-id="element-7283" data-mp="1hk0nfuz5vbb2" />
            </Field>
            <Field label="Meta title" hint="Shown in the browser tab and in search results." data-id="element-7284" data-mp="18n025x7e1tbu">
              <Input value={draft.metaTitle} onChange={e => set({
            ...draft,
            metaTitle: e.target.value
          })} data-id="element-7285" data-mp="1dl1iwbhcdgk5" />
            </Field>
            <Field label="Site URL" data-id="element-7286" data-mp="25bzatdbvwx4k">
              <Input value={draft.siteUrl} onChange={e => set({
            ...draft,
            siteUrl: e.target.value
          })} data-id="element-7287" data-mp="25awj536qmqnl" />
            </Field>
            <Field label="Search engines" data-id="element-7288" data-mp="1w9yfwn44in62">
              <Select value={draft.robots} onChange={e => set({
            ...draft,
            robots: e.target.value as SeoSettings['robots']
          })} data-id="element-7289" data-mp="1wcubi4si0zrc">
                <option value="index,follow" data-id="element-7290" data-mp="kx5oxej5q2r2">Allow indexing</option>
                <option value="noindex,nofollow" data-id="element-7291" data-mp="dbjx770zdrof">Do not index (staging)</option>
              </Select>
            </Field>
          </div>
          <Field label="Meta description" data-id="element-7292" data-mp="8cz0viouns59">
            <Textarea rows={3} value={draft.metaDescription} onChange={e => set({
          ...draft,
          metaDescription: e.target.value
        })} data-id="element-7293" data-mp="mdjhq9b7fdw4" />
          </Field>
          <div className="grid gap-4 md:grid-cols-2" data-id="element-7294" data-mp="3pt8hqy4mnumy">
            <Field label="Sharing title" hint="Falls back to the meta title when empty." data-id="element-7295" data-mp="3prnl92yr61mg">
              <Input value={draft.socialTitle} onChange={e => set({
            ...draft,
            socialTitle: e.target.value
          })} data-id="element-7296" data-mp="3ltznzt9l7wre" />
            </Field>
            <Field label="Sharing description" data-id="element-7297" data-mp="3us1jjtajxx7a">
              <Input value={draft.socialDescription} onChange={e => set({
            ...draft,
            socialDescription: e.target.value
          })} data-id="element-7298" data-mp="3738w6l7zwxoa" />
            </Field>
          </div>
        </div>}
    </SectionForm>;
}

/* --------------------------------------------------------- 3. branding */

function BrandingSection() {
  const {
    config
  } = usePlatformConfig();
  return <div className="space-y-6" data-id="element-7299" data-mp="1fr2okf531xm9">
      <SectionForm<SeoSettings> section="branding" permission="platform.settings.manage" title="Branding" description="The logo, favicon and sharing image. Upload a file or point at one already hosted." live={config.seo} summarise={draft => diffOf(config.seo as never, draft as never)} data-id="element-7300" data-mp="1frvmqrpz7ess">
        {(draft, set) => <div className="space-y-6" data-id="element-7301" data-mp="wztdw2jkt2t3">
            <AssetRow kind="logo" title="Platform logo" note="Shown in navigation and on authentication screens." url={draft.logoUrl} onChange={url => set({
          ...draft,
          logoUrl: url
        })} data-id="element-7302" data-mp="wyrpkwdjujtf" />
            <AssetRow kind="favicon" title="Favicon" note="The small icon in the browser tab. PNG or ICO, square." url={draft.faviconUrl} onChange={url => set({
          ...draft,
          faviconUrl: url
        })} data-id="element-7303" data-mp="2cgbpjpi3jknu" />
            <AssetRow kind="social" title="Sharing image" note="Used when a Campuzon link is pasted into a message or a post." url={draft.socialImageUrl} onChange={url => set({
          ...draft,
          socialImageUrl: url
        })} data-id="element-7304" data-mp="3o6k9fn59e0gu" />
          </div>}
      </SectionForm>

      <Panel title="What is accepted" description="Enforced here for your benefit, and again on the server — which is the check that counts." data-id="element-7305" data-mp="398rw91n6po5t">
        <ul className="space-y-2 text-body-sm text-ink-muted" data-id="element-7306" data-mp="359ss3j9a1vff">
          <li data-id="element-7307" data-mp="30afv45ooz7z4">PNG, JPEG, WebP or ICO, up to {(MAX_BRANDING_BYTES / 1024 / 1024).toFixed(0)} MB.</li>
          <li data-id="element-7308" data-mp="13g2ze0eveiye">
            SVG is refused. It is a script container as much as an image format, and a logo is not
            worth an injection route.
          </li>
          <li data-id="element-7309" data-mp="7bw3oatcgr7q">
            Filenames are sanitised and the file is stored by the storage service under a generated
            name, so an uploaded name can never become a path.
          </li>
          {!SETTINGS_ARE_SERVER_BACKED && <li className="text-warn-800" data-id="element-7310" data-mp="3trgtid38yuwg">
              No storage service is reachable, so a chosen file is held in this browser only and is
              not an upload. It will not appear for anyone else.
            </li>}
        </ul>
      </Panel>
    </div>;
}
function AssetRow({
  kind,
  title,
  note,
  url,
  onChange
}: {
  kind: 'logo' | 'favicon' | 'social';
  title: string;
  note: string;
  url: string;
  onChange: (url: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  async function choose(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setFailure(null);
    try {
      const asset = await uploadBranding(kind, file);
      onChange(asset.url);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'The file could not be used.');
    } finally {
      setBusy(false);
    }
  }
  return <div className="rounded-lg border border-line p-4" data-id="element-7311" data-mp="3886llb1y80v3">
      <div className="flex flex-wrap items-start gap-4" data-id="element-7312" data-mp="389re6v4pd9qb">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line bg-canvas" data-id="element-7313" data-mp="33hge9s9ypnxr">
          {url ? <img src={url} alt={`${title} preview`} className="max-h-full max-w-full object-contain" data-id="element-7314" data-mp="3hb7mmxbyj1rw" /> : <ImageIcon aria-hidden className="h-6 w-6 text-ink-subtle" data-id="element-7315" data-mp="3r7atgghr7ol6" />}
        </div>
        <div className="min-w-0 flex-1 space-y-3" data-id="element-7316" data-mp="3m87undsdaeln">
          <div data-id="element-7317" data-mp="3m8r383w4nqn0">
            <p className="text-body font-semibold text-ink" data-id="element-7318" data-mp="3m8zk06vdfg3h">{title}</p>
            <p className="text-body-sm text-ink-muted" data-id="element-7319" data-mp="1wu2r83rg8api">{note}</p>
          </div>
          <Field label="Image URL" data-id="element-7320" data-mp="1ruyitfccmh08">
            <Input value={url} onChange={event => onChange(event.target.value)} data-id="element-7321" data-mp="1rvrkw30d4ic1" />
          </Field>
          <div className="flex flex-wrap items-center gap-3" data-id="element-7322" data-mp="3kxp7tx0s8wez">
            <label className="inline-flex min-h-touch cursor-pointer items-center gap-2 rounded-lg border border-line px-3 text-body-sm font-medium text-ink" data-id="element-7323" data-mp="3g17490h6v448">
              <UploadIcon aria-hidden className="h-4 w-4" data-id="element-7324" data-mp="3ur60gxp6o5h2" />
              {busy ? 'Reading…' : 'Choose a file'}
              <input type="file" className="sr-only" accept={ALLOWED_BRANDING_TYPES.join(',')} onChange={event => choose(event.target.files?.[0])} data-id="element-7325" data-mp="320nwzvkjs234" />
            </label>
            {url && <Button variant="ghost" onClick={() => onChange('')} data-id="element-7326" data-mp="3bwr0b4r4y6rn">
                Remove
              </Button>}
          </div>
          {failure && <p className="text-body-sm text-danger-700" data-id="element-7327" data-mp="r7tjsabeywsx">{failure}</p>}
        </div>
      </div>
    </div>;
}

/* ------------------------------------------------------ 4. localisation */

const TIMEZONES = ['Africa/Lagos', 'Africa/Accra', 'Africa/Nairobi', 'Africa/Johannesburg', 'Europe/London', 'UTC'];
function LocalizationSection() {
  const {
    config
  } = usePlatformConfig();
  return <SectionForm<LocalizationSettings> section="localization" permission="platform.settings.manage" title="Localisation" description="How dates, money and measurements are presented. Nigeria-first defaults — defaults only." live={config.localization} summarise={draft => diffOf(config.localization as never, draft as never)} data-id="element-7328" data-mp="kte92xxofsdz">
      {(draft, set) => <div className="grid gap-4 md:grid-cols-2" data-id="element-7329" data-mp="13s0723xwd3do">
          <Field label="Default timezone" data-id="element-7330" data-mp="13pe2md6az3zo">
            <Select value={draft.timezone} onChange={e => set({
          ...draft,
          timezone: e.target.value
        })} data-id="element-7331" data-mp="17s0qy43rm6s4">
              {TIMEZONES.map(zone => <option key={zone} value={zone} data-id="element-7332" data-mp="209d0uymzlbs2">
                  {zone}
                </option>)}
            </Select>
          </Field>
          <Field label="Currency" data-id="element-7333" data-mp="2a2zdfb6yvdr8">
            <Select value={draft.currency} onChange={e => set({
          ...draft,
          currency: e.target.value
        })} data-id="element-7334" data-mp="262youxq5lm6g">
              {['NGN', 'GHS', 'KES', 'ZAR', 'USD', 'GBP'].map(code => <option key={code} value={code} data-id="element-7335" data-mp="1w496z4y9c1q7">
                  {code}
                </option>)}
            </Select>
          </Field>
          <Field label="Locale" data-id="element-7336" data-mp="1r8263qkpxgf4">
            <Select value={draft.locale} onChange={e => set({
          ...draft,
          locale: e.target.value
        })} data-id="element-7337" data-mp="1mf93b88t8i5o">
              {['en-NG', 'en-GB', 'en-US', 'fr-FR'].map(locale => <option key={locale} value={locale} data-id="element-7338" data-mp="1det0m0zw5f1v">
                  {locale}
                </option>)}
            </Select>
          </Field>
          <Field label="Country" data-id="element-7339" data-mp="18f80vocwd2o3">
            <Input value={draft.country} onChange={e => set({
          ...draft,
          country: e.target.value
        })} data-id="element-7340" data-mp="13gn11q1k4l06" />
          </Field>
          <Field label="Date format" data-id="element-7341" data-mp="td80jdc4mcjw">
            <Select value={draft.dateFormat} onChange={e => set({
          ...draft,
          dateFormat: e.target.value as LocalizationSettings['dateFormat']
        })} data-id="element-7342" data-mp="tc6lkxrzp5ww">
              <option value="d MMM yyyy" data-id="element-7343" data-mp="138tapyrcmj4q">17 Aug 2026</option>
              <option value="dd/MM/yyyy" data-id="element-7344" data-mp="188nyhtftuslk">17/08/2026</option>
              <option value="MM/dd/yyyy" data-id="element-7345" data-mp="amguaknfr29q">08/17/2026</option>
              <option value="yyyy-MM-dd" data-id="element-7346" data-mp="ao1qeepeet9p">2026-08-17</option>
            </Select>
          </Field>
          <Field label="Time format" data-id="element-7347" data-mp="fll2p92jcvz0">
            <Select value={draft.timeFormat} onChange={e => set({
          ...draft,
          timeFormat: e.target.value as LocalizationSettings['timeFormat']
        })} data-id="element-7348" data-mp="kko9axy5za93">
              <option value="24h" data-id="element-7349" data-mp="3o1wpldd8u21p">14:30</option>
              <option value="12h" data-id="element-7350" data-mp="3nz0qvmtkn9c7">2:30 pm</option>
            </Select>
          </Field>
          <Field label="Week starts on" data-id="element-7351" data-mp="3syn07l6a45fq">
            <Select value={draft.weekStartsOn} onChange={e => set({
          ...draft,
          weekStartsOn: e.target.value as LocalizationSettings['weekStartsOn']
        })} data-id="element-7352" data-mp="1yjq0vh3ekcic">
              <option value="monday" data-id="element-7353" data-mp="28bm1ozwvigq9">Monday</option>
              <option value="sunday" data-id="element-7354" data-mp="23i195ne1ueyg">Sunday</option>
            </Select>
          </Field>
          <Field label="Measurements" hint="Used for distance to campus." data-id="element-7355" data-mp="2h9plnrs7n5fy">
            <Select value={draft.measurement} onChange={e => set({
          ...draft,
          measurement: e.target.value as LocalizationSettings['measurement']
        })} data-id="element-7356" data-mp="2d95owpbju3uc">
              <option value="metric" data-id="element-7357" data-mp="2m9ainwntl3q1">Metric (km)</option>
              <option value="imperial" data-id="element-7358" data-mp="2m7r72jd3zgul">Imperial (mi)</option>
            </Select>
          </Field>
        </div>}
    </SectionForm>;
}

/* ----------------------------------------------------------- 5. security */

function SecuritySection() {
  const {
    security
  } = usePlatformConfig();
  return <div className="space-y-6" data-id="element-7359" data-mp="38hzjl83olbvu">
      <Panel title="Secrets" description="Generated during installation and held server-side." data-id="element-7360" data-mp="38i9107anuapx">
        <p className="text-body-sm text-ink-muted" data-id="element-7361" data-mp="2ttuzbzozcsay">
          The session secret and encryption key were generated from a cryptographic random source at
          installation. They are not shown here, and no endpoint returns them — anything that can be
          displayed can be captured. Rotating them is a server-side operation, deliberately not a
          button in a browser.
        </p>
      </Panel>

      <SectionForm<SecuritySettings> section="security" permission="platform.security.manage" title="Security policy" description="Applies to every account on this deployment. Tightening it does not sign anyone out; it applies at their next sign-in." live={security} validate={draft => draft.passwordMinLength < 8 ? 'A password floor below 8 characters is not defensible. 12 or more is recommended.' : null} summarise={draft => diffOf(security as never, draft as never)} data-id="element-7362" data-mp="2avac4iq7m72l">
        {(draft, set) => <div className="space-y-4" data-id="element-7363" data-mp="2ndiv7mt0he4">
            <div className="grid gap-4 md:grid-cols-2" data-id="element-7364" data-mp="2of424xjak9k">
              <Field label="Session length (hours)" data-id="element-7365" data-mp="3urpsgf59hqf5">
                <Input type="number" value={draft.sessionHours} onChange={e => set({
              ...draft,
              sessionHours: Number(e.target.value) || 12
            })} data-id="element-7366" data-mp="vb9l0rv571ia" />
              </Field>
              <Field label="Minimum password length" data-id="element-7367" data-mp="lkeqz1bnmxma">
                <Input type="number" value={draft.passwordMinLength} onChange={e => set({
              ...draft,
              passwordMinLength: Number(e.target.value) || 12
            })} data-id="element-7368" data-mp="hjuttxr2zu05" />
              </Field>
              <Field label="Failed sign-ins before throttling" data-id="element-7369" data-mp="1997ov3bu7fjj">
                <Input type="number" value={draft.loginAttemptsBeforeThrottle} onChange={e => set({
              ...draft,
              loginAttemptsBeforeThrottle: Number(e.target.value) || 5
            })} data-id="element-7370" data-mp="2rz3rgxwtgiaz" />
              </Field>
              <Field label="API requests per minute" data-id="element-7371" data-mp="35z4v2l4gu3u5">
                <Input type="number" value={draft.apiRequestsPerMinute} onChange={e => set({
              ...draft,
              apiRequestsPerMinute: Number(e.target.value) || 120
            })} data-id="element-7372" data-mp="3awwo5igujvzx" />
              </Field>
              <Field label="Cookie policy" data-id="element-7373" data-mp="3ontcgx3qpr16">
                <Select value={draft.sameSite} onChange={e => set({
              ...draft,
              sameSite: e.target.value as SecuritySettings['sameSite']
            })} data-id="element-7374" data-mp="3omrqhxl6rk4o">
                  <option value="lax" data-id="element-7375" data-mp="2ei9oppc8oqe">SameSite=Lax</option>
                  <option value="strict" data-id="element-7376" data-mp="39hsax4m3e0c6">SameSite=Strict</option>
                </Select>
              </Field>
            </div>
            <label className="flex items-center gap-2" data-id="element-7377" data-mp="34m3a6xyc6dea">
              <input type="checkbox" className="h-5 w-5" checked={draft.requireMfaForAdmins} onChange={e => set({
            ...draft,
            requireMfaForAdmins: e.target.checked
          })} data-id="element-7378" data-mp="3iiqitxznis01" />
              <span className="text-body text-ink" data-id="element-7379" data-mp="2qqhkspo6xrlc">Require multi-factor for administrators</span>
            </label>
            <label className="flex items-center gap-2" data-id="element-7380" data-mp="2zkw20xsa7c73">
              <input type="checkbox" className="h-5 w-5" checked={draft.secureCookies} onChange={e => set({
            ...draft,
            secureCookies: e.target.checked
          })} data-id="element-7381" data-mp="2zmgumhv1cl2b" />
              <span className="text-body text-ink" data-id="element-7382" data-mp="232zs8rx3qphb">
                Secure cookies only — refuse to set a session over plain HTTP
              </span>
            </label>
          </div>}
      </SectionForm>
    </div>;
}

/* -------------------------------------------------------- 6. admin access */

function AdminAccessSection() {
  const {
    config
  } = usePlatformConfig();
  const verdict = checkAdminSlug(config.adminSlug);
  return <div className="space-y-6" data-id="element-7383" data-mp="1o11csyrhw5i6">
      <Panel title="Where administration answers" description="Chosen at installation, changeable here." data-id="element-7384" data-mp="1nxvnpjiv93mg">
        <p className="cz-num text-h3 text-ink" data-id="element-7385" data-mp="2bnhpmeqmhwy5">/{config.adminSlug}</p>
        <p className="mt-2 text-body-sm text-ink-muted" data-id="element-7386" data-mp="2go5b6kw31lkz">
          A private administration URL keeps this console out of automated scans. It is obscurity,
          not authorisation: reaching it by any address still requires a provisioned internal staff
          link, authentication, and the permission for the action being attempted.
        </p>
        {!verdict.ok && <p className="mt-2 text-body-sm text-danger-700" data-id="element-7387" data-mp="vcdn8qzpqtyt">
            The stored slug is no longer valid: {verdict.reason}
          </p>}
      </Panel>

      <SectionForm<{
      adminSlug: string;
    }> section="admin-route" permission="platform.route.manage" title="Administration URL" description="The previous URL stops working the moment this is saved. Anyone with it open keeps their session but must use the new address." live={{
      adminSlug: config.adminSlug
    }} validate={draft => checkAdminSlug(draft.adminSlug).reason} summarise={draft => draft.adminSlug === config.adminSlug ? [] : [{
      field: 'adminSlug',
      from: `/${config.adminSlug}`,
      to: `/${draft.adminSlug}`
    }]} data-id="element-7388" data-mp="3t21eok5zy5b6">
        {(draft, set) => <div className="space-y-4" data-id="element-7389" data-mp="3k24vuz6ag0o1">
            <Field label="New administration URL" hint="Lower-case letters, numbers and hyphens. Something guessable defeats the point." data-id="element-7390" data-mp="3k36ateqfd7b1">
              <div className="flex items-center gap-2" data-id="element-7391" data-mp="2rj7l5xa5sghs">
                <span className="text-body text-ink-muted" data-id="element-7392" data-mp="2k1t1rwrs703o">/</span>
                <Input value={draft.adminSlug} onChange={event => set({
              adminSlug: event.target.value.trim().toLowerCase()
            })} data-id="element-7393" data-mp="2f2gdr8p6bmzi" />
              </div>
            </Field>
            <div className="rounded-lg border border-warn-200 bg-warn-50 p-4" data-id="element-7394" data-mp="253qubbhmpuh5">
              <p className="text-body-sm font-semibold text-ink" data-id="element-7395" data-mp="213qfvquj5ja3">Before you save this</p>
              <p className="mt-1 text-body-sm text-ink-muted" data-id="element-7396" data-mp="1wbovfhn7km8z">
                Write the new address down. If it is lost, recovery is deliberately not available
                through any public screen — a recovery screen would hand the address to whoever asked
                for it. It is restored on the server:
              </p>
              <pre className="mt-2 overflow-x-auto rounded bg-white p-2 text-caption text-ink" data-id="element-7397" data-mp="1hdwj0ycykdz0">
{`npm run admin:reset-route            # prints the current slug
npm run admin:reset-route -- <slug>   # sets a new one`}
              </pre>
            </div>
          </div>}
      </SectionForm>

      <Panel title="Reserved addresses" description="These cannot be chosen, because something else already answers there." data-id="element-7398" data-mp="17sdfr4bks66w">
        <div className="flex flex-wrap gap-1.5" data-id="element-7399" data-mp="ttnfxgxdfseu">
          {RESERVED_ROUTES.map(route => <span key={route} className="cz-num rounded border border-line px-1.5 py-0.5 text-caption text-ink-muted" data-id="element-7400" data-mp="ys8ihn03io6g">
              /{route}
            </span>)}
        </div>
      </Panel>
    </div>;
}

/* ------------------------------------------------------------ 7. database */

function DatabaseSection() {
  const [health, setHealth] = useState<DatabaseHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const check = React.useCallback(async () => {
    setLoading(true);
    try {
      setHealth(await databaseHealth());
    } catch (error) {
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    check();
  }, [check]);
  return <PermissionGate permission="platform.settings.manage" label="view database health" data-id="element-7401" data-mp="2fhznyzslwn3r">
      <div className="space-y-6" data-id="element-7402" data-mp="2t94ftfpqgb3j">
        <Panel title="Database" description="Health and schema state. Credentials belong to deployment, not to a browser." action={<Button variant="secondary" icon={<ActivityIcon className="h-4 w-4" data-id="element-7405" data-mp="1wk6jht2c4549" />} onClick={check} loading={loading} data-id="element-7404" data-mp="20mjrko4k0f47">
              Test connection
            </Button>} data-id="element-7403" data-mp="2tca44sqjf8y7">
          {health ? <>
              <div className="flex flex-wrap items-center gap-3" data-id="element-7406" data-mp="25jjyf1989lwd">
                <Badge tone={health.connected ? 'ok' : 'danger'} data-id="element-7407" data-mp="3lyl544i23bvj">
                  {health.connected ? 'Connected' : 'Not connected'}
                </Badge>
                <p className="min-w-0 text-body-sm text-ink-muted" data-id="element-7408" data-mp="3gzibrhbfuu3e">{health.message}</p>
              </div>
              <dl className="mt-4 divide-y divide-line rounded-lg border border-line" data-id="element-7409" data-mp="1qkr5nolhr7w4">
                {[['Engine', health.engine], ['Server version', health.serverVersion ?? '—'], ['Host', health.hostMasked], ['Database', health.databaseName], ['Schema version', health.schemaVersion ?? '—'], ['Last migration', health.lastMigration ?? '—'], ['Pending migrations', health.pendingMigrations.length ? health.pendingMigrations.join(', ') : 'None outstanding'], ['Round trip', health.latencyMs === null ? '—' : `${health.latencyMs} ms`]].map(([term, value]) => <div key={term} className="grid gap-1 p-3 sm:grid-cols-[200px_1fr]" data-id="element-7410" data-mp="2x6tm75j6cz9k">
                    <dt className="text-body-sm font-semibold text-ink" data-id="element-7411" data-mp="ljq723px8mmi">{term}</dt>
                    <dd className="cz-num min-w-0 break-words text-body-sm text-ink-muted" data-id="element-7412" data-mp="lhn8gvapdl66">
                      {value}
                    </dd>
                  </div>)}
              </dl>
            </> : <p className="text-body-sm text-ink-muted" data-id="element-7413" data-mp="7ki18rz96g9c">
              <DatabaseIcon aria-hidden className="mr-2 inline h-4 w-4" data-id="element-7414" data-mp="7m1m6vvutfhg" />
              {loading ? 'Checking…' : 'No database report is available.'}
            </p>}
        </Panel>

        <Panel title="What this screen cannot show" description="Not hidden — absent." data-id="element-7415" data-mp="109fjiv3gsd23">
          <ul className="space-y-2 text-body-sm text-ink-muted" data-id="element-7416" data-mp="vgcqco2ln5mf">
            <li data-id="element-7417" data-mp="qjmb22ymce22">The database password, and the connection string that contains it.</li>
            <li data-id="element-7418" data-mp="1nyqe1uu81b7s">The session secret and the encryption key.</li>
            <li data-id="element-7419" data-mp="1o0bajq03j48a">
              Any credential-changing action. Database configuration is deployment configuration; it
              is changed on the server and the service restarted.
            </li>
          </ul>
        </Panel>

        <Panel title="Backup and restore" description="Architected, deliberately not exposed here." data-id="element-7420" data-mp="272k45j3nfkxd">
          <p className="text-body-sm text-ink-muted" data-id="element-7421" data-mp="1tadlnqd0x1ur">
            Backup, restore, configuration export and import run as server-side operations with their
            own authorisation. A restore reachable from a browser session is a single compromised
            session away from replacing the platform's data, so it is not offered here.
          </p>
        </Panel>
      </div>
    </PermissionGate>;
}

/* ------------------------------------------------------ 8. system information */

function SystemSection() {
  const {
    config,
    source
  } = usePlatformConfig();
  const [info, setInfo] = useState<SystemInformation | null>(null);
  useEffect(() => {
    systemInformation().then(setInfo).catch(() => setInfo(null));
  }, []);
  return <div className="space-y-6" data-id="element-7422" data-mp="x4tuskeyg1ma">
      <Panel title="System information" description="What this deployment is running." data-id="element-7423" data-mp="x5vgrjxie8is">
        <dl className="divide-y divide-line rounded-lg border border-line" data-id="element-7424" data-mp="t41ifiasdgls">
          {[['Platform', info?.platformVersion ?? 'Campuzon 2.0'], ['Front end', info?.frontendVersion ?? 'Campuzon 2.0'], ['API', info?.apiVersion ?? 'Not reachable'], ['Runtime', info?.runtimeVersion ?? '—'], ['Database', info?.databaseVersion ?? '—'], ['Schema', info?.schemaVersion ?? config.schemaVersion ?? '—'], ['Installed', info?.installedAt ?? (config.installed ? '—' : 'Not installed')], ['Environment', info?.environment ?? config.environment], ['API origin', info?.apiOrigin ?? 'Not configured'], ['Configuration source', source === 'server' ? 'Configuration service' : source === 'local' ? 'This browser' : 'Development defaults']].map(([term, value]) => <div key={term} className="grid gap-1 p-3 sm:grid-cols-[200px_1fr]" data-id="element-7425" data-mp="31pbrbw562qux">
              <dt className="text-body-sm font-semibold text-ink" data-id="element-7426" data-mp="35qnapjxg6fuo">{term}</dt>
              <dd className="cz-num min-w-0 break-words text-body-sm text-ink-muted" data-id="element-7427" data-mp="3aios1k9h2wrk">{value}</dd>
            </div>)}
        </dl>
        <p className="mt-3 flex items-start gap-2 text-caption text-ink-subtle" data-id="element-7428" data-mp="3fg6xv1s70vvc">
          <InfoIcon aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-7429" data-mp="2isanwfr9gyt3" />
          Secrets, private keys and authentication tokens are not part of this report. There is no
          field in it capable of carrying one.
        </p>
      </Panel>
    </div>;
}

/* ---------------------------------------------------------- terminology */

/**
 * The platform's business vocabulary.
 *
 * Semantic rather than find-and-replace: each concept carries its own singular,
 * plural and short forms, because "Property" does not pluralise by appending an
 * "s" and a collapsed sidebar needs a shorter word than a page heading.
 *
 * Renaming a concept changes labels only. Route ids, permissions and API
 * references are keyed to the technical id and are stated here as unaffected,
 * so nobody has to wonder whether renaming will break their bookmarks.
 */
function TerminologySection() {
  const {
    config
  } = usePlatformConfig();
  const live = config.terminology ?? {};
  return <div className="space-y-6" data-id="element-7430" data-mp="1ds2kcylafels">
      <Panel title="Why this exists" description="Business-facing words are configuration, not source code." data-id="element-7431" data-mp="1dt403gd90p9u">
        <p className="text-body-sm text-ink-muted" data-id="element-7432" data-mp="18vc9qqs95b7r">
          Every label in administration that names a business concept is resolved through this
          register. Rename <span className="font-semibold text-ink" data-id="element-7433" data-mp="ywd8ht9tghus">Housing</span> to{' '}
          <span className="font-semibold text-ink" data-id="element-7434" data-mp="2y8pg0ljxd9bi">Lodging</span> and the navigation, page titles,
          empty states and reports follow, with no code change and no rebuild.
        </p>
        <p className="mt-2 text-body-sm text-ink-muted" data-id="element-7435" data-mp="3hq8cbtbq7ai2">
          Addresses do not change. <span className="cz-num" data-id="element-7436" data-mp="3dowgv7667v1y">/admin/housing</span> stays exactly where
          it is, and permissions keep their existing keys — a label and an identifier are different
          things, so bookmarks, links and integrations are unaffected.
        </p>
      </Panel>

      <SectionForm<TerminologyOverrides> section="terminology" permission="platform.settings.manage" title="Platform terminology" description="Leave a field empty to use the shipped word. An empty field is not a blank label — it is the default." live={live} validate={draft => {
      for (const concept of TERM_CONCEPTS) {
        for (const form of ['singular', 'plural', 'short'] as const) {
          const value = draft[concept]?.[form];
          if (value && value.trim().length > 48) {
            return `${TERMINOLOGY[concept].singular}: a ${form} form longer than 48 characters will not fit the surfaces that use it.`;
          }
        }
      }
      return null;
    }} summarise={draft => {
      const changes: Array<{
        field: string;
        from: string;
        to: string;
      }> = [];
      for (const concept of TERM_CONCEPTS) {
        for (const form of ['singular', 'plural', 'short'] as const) {
          const before = resolveTerm(live, concept, form);
          const after = resolveTerm(draft, concept, form);
          if (before !== after) {
            changes.push({
              field: `${concept}.${form}`,
              from: before,
              to: after
            });
          }
        }
      }
      return changes;
    }} data-id="element-7437" data-mp="33sjp4eu6rr9u">
        {(draft, set) => <div className="space-y-3" data-id="element-7438" data-mp="1o6pwfn3d4roe">
            {TERM_CONCEPTS.map(concept => <ConceptRow key={concept} concept={concept} draft={draft} onChange={next => set(next)} data-id="element-7439" data-mp="1t7nc9t0wh9mq" />)}
          </div>}
      </SectionForm>
    </div>;
}
function ConceptRow({
  concept,
  draft,
  onChange
}: {
  concept: TermConcept;
  draft: TerminologyOverrides;
  onChange: (next: TerminologyOverrides) => void;
}) {
  const definition = TERMINOLOGY[concept];
  const overrides = draft[concept] ?? {};
  const overridden = (['singular', 'plural', 'short'] as const).some(form => Boolean(overrides[form]?.trim()));
  const set = (form: 'singular' | 'plural' | 'short', value: string) => {
    const next = {
      ...draft,
      [concept]: {
        ...overrides,
        [form]: value
      }
    };
    // A cleared field must mean "use the default", so the override is removed
    // rather than stored as an empty string that could render as a blank label.
    const forms = next[concept] as Record<string, string>;
    for (const key of Object.keys(forms)) if (!forms[key]?.trim()) delete forms[key];
    if (Object.keys(forms).length === 0) delete next[concept];
    onChange(next);
  };
  const reset = () => {
    const next = {
      ...draft
    };
    delete next[concept];
    onChange(next);
  };
  return <div className="rounded-lg border border-line p-4" data-id="element-7440" data-mp="2hi1tmmgzkpce">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-7441" data-mp="2hhi9r07rzqvq">
        <div className="min-w-0" data-id="element-7442" data-mp="23ptxmwxj12er">
          <p className="text-body font-semibold text-ink" data-id="element-7443" data-mp="23osncuoc4jy3">
            {resolveTerm(draft, concept, 'singular')}
          </p>
          <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-7444" data-mp="1tsz3eq8ado6a">{definition.meaning}</p>
          <p className="mt-1 text-caption text-ink-subtle" data-id="element-7445" data-mp="1tv201t3y2fjz">
            Identifier <span className="cz-num" data-id="element-7446" data-mp="1yt3lulsr8sz5">{definition.id}</span> — never changes, whatever this
            is called.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2" data-id="element-7447" data-mp="1pquc3y5b93mt">
          {overridden ? <>
              <Badge tone="info" plain data-id="element-7448" data-mp="3f6bnufaj68sy">
                Renamed
              </Badge>
              <Button variant="ghost" size="sm" onClick={reset} data-id="element-7449" data-mp="3k54ze269bpqh">
                Reset to default
              </Button>
            </> : <Badge tone="neutral" plain data-id="element-7450" data-mp="1k1kp4op34xl6">
              Default
            </Badge>}
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-3" data-id="element-7451" data-mp="1f4ls9ye1ecja">
        {([['singular', 'Singular'], ['plural', 'Plural'], ['short', 'Short form']] as const).map(([form, label]) => <Field key={form} label={label} hint={`Default: ${definition[form]}`} data-id="element-7452" data-mp="114jgmtwbofch">
            <Input value={overrides[form] ?? ''} placeholder={definition[form]} onChange={event => set(form, event.target.value)} data-id="element-7453" data-mp="wdb8002tgy0d" />
          </Field>)}
      </div>
    </div>;
}

/* -------------------------------------------------------------- history */

function HistoryPanel() {
  const [changes, setChanges] = useState<SettingsChange[]>([]);
  useEffect(() => {
    settingsHistory().then(setChanges).catch(() => setChanges([]));
  }, []);
  return <Panel title="Configuration history" description="Append-only, with the previous and new values. Passwords and secrets are never recorded, because they are never received." data-id="element-7454" data-mp="2p2lo4aokhkvb">
      {changes.length === 0 ? <p className="text-body-sm text-ink-muted" data-id="element-7455" data-mp="2y30brgp07c1z">
          <KeyRoundIcon aria-hidden className="mr-2 inline h-4 w-4" data-id="element-7456" data-mp="3323f8wpc5a7u" />
          No configuration changes have been recorded on this deployment yet.
        </p> : <ul className="space-y-3" data-id="element-7457" data-mp="269hpeawhshtu">
          {changes.map(change => <li key={change.id} className="rounded-lg border border-line p-3" data-id="element-7458" data-mp="2ad5wytl4jdf3">
              <div className="flex flex-wrap items-center gap-2" data-id="element-7459" data-mp="2fb7fnden1aq1">
                <Badge tone="neutral" plain data-id="element-7460" data-mp="2k38zbke4y3q3">
                  {label(change.section)}
                </Badge>
                <span className="text-body-sm font-semibold text-ink" data-id="element-7461" data-mp="1mo3ifulse0ot">{change.changedBy}</span>
                <span className="cz-num text-caption text-ink-subtle" data-id="element-7462" data-mp="1rlwpepuwyyq5">
                  {new Date(change.createdAt).toLocaleString()}
                </span>
              </div>
              <p className="mt-1 text-body-sm text-ink-muted" data-id="element-7463" data-mp="2py8wkeictws6">{change.reason}</p>
              <ul className="mt-2 space-y-1" data-id="element-7464" data-mp="2pw4snwkggxrw">
                {change.changed.map(field => <li key={field.field} className="text-caption text-ink-muted" data-id="element-7465" data-mp="33rqe5trhczfk">
                    <span className="font-semibold text-ink" data-id="element-7466" data-mp="2zou5aurqshot">{label(field.field)}</span>{' '}
                    <span className="line-through" data-id="element-7467" data-mp="2byzs2c3igr41">{field.from}</span> → {field.to}
                  </li>)}
              </ul>
            </li>)}
        </ul>}
    </Panel>;
}